/**
 * aides-agriculteurs.ts
 * ─────────────────────────────────────────────────────────────────
 * Configuration centralisée des pages "stubs" /agriculteurs/aides/[slug].
 *
 * Aides financières mobilisables par les exploitants agricoles pour
 * leurs projets d'efficacité énergétique ou de transition (serres,
 * élevage, méthanisation, photovoltaïque, irrigation…).
 */

import type { AideStub } from './aides-particuliers';

export const AIDES_AGRICULTEURS: AideStub[] = [
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'cee-agricoles',
    iconName: 'sparkles',
    eyebrow: "Certificats d'Économies d'Énergie, sectoriel AGRI",
    title: 'CEE agricoles',
    shortLabel: 'CEE agricoles',
    lead: "Des aides pour vos investissements : isolation des bâtiments d'élevage, séchage, panneaux solaires, pompes à chaleur agricoles. Versées après réalisation des travaux.",
    amount: 'Selon fiche & volume',
    amountLabel: 'Versement après travaux',
    keyPoints: [
      {
        iconName: 'sparkles',
        label: 'Sans condition de revenus',
        description:
          "Accessible à toute exploitation agricole, quelle que soit sa taille ou son chiffre d'affaires.",
      },
      {
        iconName: 'file-text',
        label: 'Fiches AGRI dédiées',
        description:
          "Catalogue d'opérations standardisées du secteur AGRI : bâtiments, équipements, chauffage, utilités.",
      },
      {
        iconName: 'hard-hat',
        label: 'Pas toujours de qualification spécifique',
        description:
          "Contrairement au résidentiel, les opérations agricoles n'exigent pas systématiquement un installateur certifié — vérifier la fiche.",
      },
      {
        iconName: 'file-signature',
        label: 'Demande avant le devis',
        description:
          "L'aide doit être demandée avant de signer le devis. Sinon, dossier non éligible.",
      },
    ],
    conditions: [
      'Exploitation agricole en activité',
      "Travaux ou équipements éligibles à une fiche d'opération AGRI applicable",
      "Demande d'aide avant signature du devis",
      'Conservation des justificatifs : devis, factures',
      'Réalisation par un professionnel disposant des qualifications requises',
    ],
    cumulableWith: ['PCAE (FEADER)', 'FranceAgriMer', 'Plan France 2030', 'Aides régionales'],
    faq: [
      {
        question: 'Quelles fiches sont disponibles pour mon exploitation ?',
        answer:
          "Les fiches AGRI couvrent : isolation des bâtiments d'élevage et des serres, équipements de froid en élevage laitier (pré-refroidissement, récupération de chaleur), variateurs de vitesse et ventilateurs haute efficacité, chaudières biomasse pour serres, pompes haute efficacité. On liste les fiches qui s'appliquent à votre exploitation et on chiffre les aides.",
      },
      {
        question: 'Les aides agricoles sont-elles cumulables avec le PCAE ?',
        answer:
          'Oui, les aides sont cumulables avec les aides régionales du PCAE, FranceAgriMer et le Plan France 2030, dans la limite des plafonds autorisés.',
      },
      {
        question: "Quel est le montant moyen d'une aide agricole ?",
        answer:
          "Très variable selon l'opération et le volume : de quelques centaines d'euros pour un variateur de vitesse à plusieurs dizaines de milliers d'euros pour une isolation de bâtiment important ou un chauffage biomasse en serre. Notre estimateur vous donne une fourchette personnalisée en 60 secondes.",
      },
    ],
    ctaLabel: 'Estimer mes CEE agricoles',
    ctaHref: '/agriculteurs/diagnostic-cee',
    secondaryCta: { label: 'Voir les fiches AGRI', href: '/cee/fiches?secteur=AGRI' },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'franceagrimer',
    iconName: 'landmark',
    eyebrow: "Établissement national des produits de l'agriculture et de la mer",
    title: 'Aides FranceAgriMer',
    shortLabel: 'FranceAgriMer',
    lead: "L'établissement public qui pilote des aides ciblées par filière : modernisation des serres, méthanisation, investissement. Chaque filière a ses propres dispositifs.",
    amount: 'Variable',
    amountLabel: 'Selon appel à projets en cours',
    keyPoints: [
      {
        iconName: 'landmark',
        label: "Établissement public d'État",
        description:
          "Sous tutelle du Ministère de l'Agriculture, FranceAgriMer applique les orientations gouvernementales par filière.",
      },
      {
        iconName: 'file-search',
        label: 'Appels à projets thématiques',
        description:
          'Aides ouvertes par fenêtre, sur des thématiques précises (méthanisation, serres décarbonées, fruits-légumes, etc.).',
      },
      {
        iconName: 'tractor',
        label: 'Filières concernées',
        description:
          'Grandes cultures, élevage, fruits et légumes, viticulture, horticulture, semences, pêche et aquaculture.',
      },
      {
        iconName: 'banknote',
        label: 'Cumul possible avec autres aides',
        description: "Dans la limite des plafonds d'intensité d'aide autorisés.",
      },
    ],
    conditions: [
      "Exploitation agricole, organisation de producteurs, GIEE ou structure éligible à l'appel à projets",
      'Projet répondant aux critères techniques et économiques publiés par FranceAgriMer',
      "Dépôt du dossier dans la fenêtre de candidature de l'appel à projets",
      'Engagement à fournir les pièces justificatives et les indicateurs de suivi',
      "Respect des règles européennes sur les aides d'État",
    ],
    cumulableWith: ['Aides agricoles', 'PCAE (FEADER)', 'Plan France 2030', 'Aides régionales'],
    faq: [
      {
        question: 'Comment savoir quels appels à projets sont ouverts ?',
        answer:
          'La liste des appels à projets en cours est publiée sur franceagrimer.fr (rubrique « Aides »). Les fenêtres de dépôt sont généralement annuelles ou pluriannuelles. Vous pouvez vous abonner à leur newsletter.',
      },
      {
        question: 'Mon exploitation est-elle éligible ?',
        answer:
          "L'éligibilité dépend de chaque appel à projets : taille de l'exploitation, filière, localisation, nature du projet, performance énergétique visée. Lisez le règlement de l'appel ou faites-vous accompagner par votre Chambre d'Agriculture.",
      },
      {
        question: "Qu'est-ce que le Plan de souveraineté Fruits et Légumes ?",
        answer:
          "C'est un dispositif lancé en 2023 dans le cadre du Plan France 2030, géré par FranceAgriMer, qui soutient les investissements de la filière fruits-légumes (serres, équipements de tri/conditionnement, automatisation).",
      },
    ],
    ctaLabel: 'Identifier mes aides FranceAgriMer',
    ctaHref: '/agriculteurs/diagnostic-cee',
    secondaryCta: {
      label: 'Demander à être rappelé(e)',
      href: '/contact?source=agriculteurs&besoin=franceagrimer',
    },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'plan-france-2030',
    iconName: 'banknote',
    eyebrow: 'Investissement public massif pour la transition',
    title: 'Plan France 2030 — Volet Agriculture',
    shortLabel: 'France 2030',
    lead: 'Des fonds publics pour accélérer la transition agricole : équipements de précision, robotique, décarbonation des serres.',
    amount: 'Programme pluriannuel',
    amountLabel: 'Subventions, avances remboursables, prêts',
    keyPoints: [
      {
        iconName: 'banknote',
        label: 'Plusieurs guichets',
        description:
          'Bpifrance (innovation et transition), FranceAgriMer (filières), ADEME (décarbonation).',
      },
      {
        iconName: 'sparkles',
        label: 'Subventions + avances remboursables',
        description:
          'Aide directe ou avance à rembourser uniquement en cas de succès commercial du projet.',
      },
      {
        iconName: 'tractor',
        label: 'Projets de modernisation',
        description:
          'Robotique agricole, agriculture de précision, serres décarbonées, méthanisation, valorisation biomasse.',
      },
      {
        iconName: 'users',
        label: 'Souvent en collectif',
        description:
          'Projets portés par des coopératives, GIEE, structures de filière — plutôt que par une exploitation individuelle.',
      },
    ],
    conditions: [
      'Projet correspondant aux priorités sectorielles du Plan France 2030 Agriculture',
      "Dossier déposé dans le cadre d'un appel à projets identifié (Bpifrance, FAM ou ADEME)",
      'Caractère innovant ou exemplaire du projet (recherche, démonstration, déploiement)',
      'Capacité à apporter un cofinancement propre (variable selon le dispositif)',
      "Respect des règles européennes sur les aides d'État",
    ],
    cumulableWith: ['Aides agricoles', 'PCAE (FEADER)', 'FranceAgriMer', 'Aides régionales'],
    faq: [
      {
        question: 'Comment candidater au Plan France 2030 ?',
        answer:
          "Le Plan France 2030 ne dispose pas d'un guichet unique : chaque appel à projets est géré par un opérateur (Bpifrance, FranceAgriMer, ADEME, etc.). Pour les projets agricoles, surveillez les appels publiés sur franceagrimer.fr, bpifrance.fr/France2030 et ademe.fr.",
      },
      {
        question: 'Mon investissement individuel peut-il être financé ?',
        answer:
          "Plus rarement. Le Plan France 2030 cible principalement des projets collectifs ou innovants à fort effet d'entraînement. Pour un investissement individuel courant, orientez-vous plutôt vers les aides agricoles, le PCAE ou les aides régionales.",
      },
      {
        question: 'Quelle différence avec le PCAE ?',
        answer:
          'Le PCAE finance des investissements matériels « courants » de modernisation, via les Conseils Régionaux et le FEADER. Le Plan France 2030 cible des projets plus structurants (innovation, démonstration, filière), via des opérateurs nationaux.',
      },
    ],
    ctaLabel: 'Étudier mon éligibilité',
    ctaHref: '/contact?source=agriculteurs&besoin=france2030',
    secondaryCta: { label: 'Voir les fiches CEE éligibles', href: '/cee/fiches?secteur=AGRI' },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'pcae',
    iconName: 'tractor',
    eyebrow: "Plan de Compétitivité et d'Adaptation des Exploitations",
    title: 'PCAE (FEADER régional)',
    shortLabel: 'PCAE',
    lead: "La principale aide à l'investissement pour les exploitations. Cofinancée par votre Région et l'Europe : modernisation des bâtiments, équipements, énergies renouvelables.",
    amount: 'Taux 20 – 40 %',
    amountLabel: 'Du coût HT éligible, selon zonage et jeune agriculteur',
    keyPoints: [
      {
        iconName: 'tractor',
        label: 'Géré par votre Conseil Régional',
        description:
          "Chaque région définit ses propres modalités (critères, plafonds, équipements éligibles). Variable d'une région à l'autre.",
      },
      {
        iconName: 'banknote',
        label: 'Taux majoré pour Jeunes Agriculteurs',
        description:
          'Bonus pour les exploitants en première installation et pour les zones défavorisées simples ou de montagne.',
      },
      {
        iconName: 'hammer',
        label: 'Bâtiments et équipements',
        description:
          "Bâtiments d'élevage, hangars de stockage, équipements de transformation, photovoltaïque sur toiture, méthanisation.",
      },
      {
        iconName: 'sparkles',
        label: 'Cumul avec aides possible',
        description:
          "Les aides peuvent s'additionner au PCAE dans la limite des plafonds autorisés.",
      },
    ],
    conditions: [
      'Exploitation agricole inscrite à la MSA, située dans la région concernée',
      'Investissement éligible selon la grille technique de la Région',
      "Dépôt du dossier auprès du guichet régional dans la fenêtre d'appel",
      "Engagement à maintenir l'investissement en exploitation pendant 5 ans minimum",
      "Plancher et plafond d'investissement variables (généralement 4 000 € HT plancher, plusieurs centaines de milliers d'euros plafond)",
    ],
    cumulableWith: ['Aides agricoles', 'FranceAgriMer', 'Plan France 2030', 'Aides locales'],
    faq: [
      {
        question: 'Comment connaître les critères de ma région ?',
        answer:
          "Chaque Conseil Régional publie son propre cahier des charges PCAE, généralement disponible sur son site internet (rubrique « Agriculture et alimentation »). Vous pouvez aussi vous rapprocher de votre Chambre d'Agriculture.",
      },
      {
        question: "Quels sont les taux d'aide PCAE ?",
        answer:
          'Très variables selon les régions et les profils. En général : 20 à 25 % pour un exploitant établi, majoré de 5 à 15 points pour les Jeunes Agriculteurs (DJA), les zones défavorisées ou de montagne. Le cumul ne peut excéder les plafonds autorisés.',
      },
      {
        question: 'Le PCAE finance-t-il le photovoltaïque ?',
        answer:
          "Cela dépend des régions. Certaines excluent les projets photovoltaïques du PCAE. À noter : le photovoltaïque n'est pas couvert par l'aide travaux — orientez-vous vers les appels à projets FranceAgriMer, ADEME ou Plan France 2030 pour ce type d'investissement.",
      },
    ],
    ctaLabel: 'Calculer mes aides agricoles',
    ctaHref: '/agriculteurs/diagnostic-cee',
    secondaryCta: {
      label: "Contacter ma Chambre d'Agriculture",
      href: '/contact?source=agriculteurs&besoin=pcae',
    },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'aides-locales',
    iconName: 'landmark',
    eyebrow: 'Conseils régionaux, départementaux, EPCI',
    title: 'Aides locales agricoles',
    shortLabel: 'Aides locales',
    lead: 'En plus des aides nationales, votre Région, votre Département ou votre communauté de communes proposent souvent leurs propres dispositifs. Variables selon votre territoire.',
    amount: 'Variable',
    amountLabel: 'Selon territoire et dispositif local',
    keyPoints: [
      {
        iconName: 'landmark',
        label: 'Multi-guichets locaux',
        description:
          "Conseil régional, département, EPCI, syndicats mixtes, agences de l'eau, MSA, Chambres d'Agriculture.",
      },
      {
        iconName: 'droplet',
        label: "Aides Agences de l'eau",
        description:
          "Pour les économies d'eau d'irrigation, retenues collinaires, ou récupération d'eaux pluviales — selon le bassin.",
      },
      {
        iconName: 'tractor',
        label: 'Bonus jeune agriculteur',
        description:
          'De nombreuses collectivités majorent leurs aides pour les exploitants en installation.',
      },
      {
        iconName: 'users',
        label: 'Soutien aux GIEE et coopératives',
        description:
          "Certaines régions soutiennent prioritairement les projets collectifs (Groupements d'Intérêt Économique et Environnemental).",
      },
    ],
    conditions: [
      "Localisation de l'exploitation dans la zone géographique éligible",
      'Projet correspondant aux orientations du dispositif local',
      "Dépôt du dossier dans les fenêtres d'appel à projets de la collectivité",
      'Cumul avec les aides nationales soumis aux plafonds autorisés',
      'Respect des règles spécifiques au financeur (durabilité, indicateurs de suivi)',
    ],
    cumulableWith: ['Aides agricoles', 'PCAE (FEADER)', 'FranceAgriMer', 'Plan France 2030'],
    faq: [
      {
        question: 'Comment trouver les aides de ma région ?',
        answer:
          "Trois bonnes pistes : (1) le portail aides-entreprises.fr (cofinancé par les Régions) ; (2) le site de votre Conseil Régional ou Conseil Départemental, rubrique Agriculture ; (3) votre Chambre d'Agriculture qui dispose de conseillers aides locales.",
      },
      {
        question: "Les aides de l'Agence de l'eau sont-elles cumulables ?",
        answer:
          "Oui, généralement. Les aides des Agences de l'eau ciblent les économies d'eau et la qualité de la ressource. Elles peuvent compléter un PCAE qui finance un système d'irrigation, dans la limite des plafonds autorisés.",
      },
      {
        question: 'Que finance la MSA ?',
        answer:
          "La Mutualité Sociale Agricole peut financer des actions de prévention des risques professionnels (équipements de protection, aménagement de poste) et propose ponctuellement des dispositifs d'accompagnement pour les exploitations en difficulté ou en transmission.",
      },
    ],
    ctaLabel: 'Identifier mes aides locales',
    ctaHref: '/contact?source=agriculteurs&besoin=aides-locales',
    secondaryCta: { label: 'Voir le PCAE de ma région', href: '/agriculteurs/aides/pcae' },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'optimisation-turpe',
    iconName: 'trending-down',
    eyebrow: "Tarif d'acheminement de l'électricité,",
    title: 'Optimisation TURPE pour votre exploitation',
    shortLabel: 'Optimisation TURPE',
    lead: "Le transport de l'électricité est la part « réseau » de vos factures — souvent un quart à un tiers du total. Sur une exploitation agricole, les consommations sont très saisonnières (irrigation, séchage, chauffage de serre) : des puissances souscrites calées sur la pointe de saison se paient toute l'année. Ajuster les paramètres de chaque compteur génère des économies récurrentes, sans travaux ni changement de fournisseur.",
    amount: 'Économies récurrentes',
    amountLabel: 'Sur la part acheminement de chaque compteur',
    keyPoints: [
      {
        iconName: 'gauge',
        label: 'Puissances calées sur vos saisons',
        description:
          "Irrigation l'été, séchage à l'automne, serres l'hiver : on compare les puissances souscrites aux appels réels de chaque période pour ne plus payer une pointe de saison toute l'année.",
      },
      {
        iconName: 'tractor',
        label: "Tous les compteurs de l'exploitation",
        description:
          'Hangar, salle de traite, station de pompage, chambre froide : chaque point de livraison a ses propres paramètres — on les passe tous en revue.',
      },
      {
        iconName: 'trending-down',
        label: 'Dépassements évités',
        description:
          "Les dépassements de puissance (démarrage de pompes, pointes de traite) sont facturés avec pénalité. On arbitre entre relever la souscription et lisser l'usage.",
      },
      {
        iconName: 'wallet',
        label: "Indépendant des fournisseurs d'énergie",
        description:
          'Le transport est identique chez tous les fournisseurs : cette optimisation se cumule avec la comparaison des offres pour votre exploitation.',
      },
    ],
    conditions: [
      'Analyse des 12 derniers mois de factures de chaque compteur (ou courbes de charge)',
      "Applicable quel que soit votre fournisseur d'électricité",
      'Modification des paramètres via le fournisseur (répercutée au gestionnaire de réseau)',
      'Étude des pénalités de dépassement poste par poste (pompage, froid, séchage)',
    ],
    cumulableWith: [
      'Comparateur énergie exploitation',
      'Aides agricoles',
      'PCAE',
      'Exonération CSPE',
    ],
    faq: [
      {
        question: "C'est quoi le TURPE sur mes factures ?",
        answer:
          "C'est le péage payé pour l'utilisation du réseau public d'électricité, reversé au gestionnaire de réseau (Enedis). Il est fixé par la Commission de régulation de l'énergie et apparaît dans la part « acheminement » de chaque facture. Il ne se négocie pas, mais ses paramètres (puissances souscrites, formule tarifaire) dépendent de votre contrat — c'est là que se trouvent les économies.",
      },
      {
        question: 'Mes consommations sont très saisonnières, est-ce un avantage ?',
        answer:
          "Oui, c'est précisément le cas où l'optimisation rapporte le plus. Une station d'irrigation qui tourne 4 mois par an n'a pas besoin de la même puissance souscrite toute l'année, et certaines formules tarifaires valorisent les usages concentrés sur des périodes creuses.",
      },
      {
        question: 'Faut-il changer de fournisseur ?',
        answer:
          "Non. Le transport est strictement identique chez tous les fournisseurs : l'optimisation passe par un simple ajustement des paramètres de vos points de livraison. On en profite souvent pour comparer aussi les offres de fourniture — les deux économies se cumulent.",
      },
    ],
    ctaLabel: 'Auditer mes factures gratuitement',
    ctaHref: '/agriculteurs/comparateur-energie',
    secondaryCta: {
      label: 'En parler avec un conseiller',
      href: '/contact?source=agriculteurs&besoin=optimisation-turpe',
    },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'exoneration-cspe',
    iconName: 'percent',
    eyebrow: "Fiscalité de l'électricité, taux réduits et exonérations,",
    title: "Exonération CSPE (accise sur l'électricité)",
    shortLabel: 'Exonération CSPE',
    lead: "La taxe sur l'électricité se paie sur chaque mégawattheure consommé par votre exploitation. Les exploitations fortement consommatrices — serres chauffées, froid agricole, irrigation intensive — peuvent relever des taux réduits prévus pour les entreprises électro-intensives. Et si le taux plein a été appliqué à tort, le trop-versé se récupère par réclamation.",
    amount: 'Taux réduit possible',
    amountLabel: "Selon l'intensité électrique de l'exploitation",
    keyPoints: [
      {
        iconName: 'gauge',
        label: 'Électro-intensité calculée',
        description:
          "On rapporte votre consommation d'électricité à la valeur ajoutée de l'exploitation : c'est ce ratio qui ouvre droit aux taux réduits.",
      },
      {
        iconName: 'sparkles',
        label: 'Profils agricoles concernés',
        description:
          "Serres chauffées et éclairées, froid agricole (tank, chambres froides), irrigation et pompage intensifs : ce sont les profils où l'électro-intensité est la plus fréquente.",
      },
      {
        iconName: 'banknote',
        label: 'Remboursement rétroactif',
        description:
          'Si vous étiez éligible mais facturé au taux plein, une réclamation permet de récupérer le trop-versé sur les années récentes.',
      },
      {
        iconName: 'file-signature',
        label: 'Démarche déclarative simple',
        description:
          "Une attestation transmise à votre fournisseur applique le bon taux ; la régularisation du passé se traite auprès de l'administration fiscale. On prépare le dossier avec vous.",
      },
    ],
    conditions: [
      "Calcul du ratio d'électro-intensité à partir de vos factures et de vos données comptables",
      'Seuils et taux fixés par les textes applicables',
      'Attestation transmise au fournisseur pour les factures à venir',
      'Justificatifs conservés pour les demandes de remboursement rétroactif',
    ],
    cumulableWith: [
      'Optimisation TURPE',
      'Comparateur énergie exploitation',
      'Aides agricoles',
      'PCAE',
    ],
    faq: [
      {
        question: 'Mon exploitation peut-elle être « électro-intensive » ?',
        answer:
          "Oui, plus souvent qu'on ne le croit : une serre chauffée et éclairée, un atelier de froid agricole ou une station de pompage importante peuvent suffire à franchir les seuils, car la valeur ajoutée d'une exploitation est souvent modeste au regard de sa facture d'électricité. Le calcul se fait à partir de vos chiffres réels.",
      },
      {
        question: 'CSPE, TICFE, accise : quelle différence ?',
        answer:
          "Aucune en pratique : c'est la même taxe sous trois noms successifs. L'ancienne CSPE a été fusionnée dans la TICFE, rebaptisée « accise sur l'électricité ». Elle figure sur chaque facture, proportionnelle aux MWh consommés.",
      },
      {
        question: 'Puis-je récupérer le trop-payé des années passées ?',
        answer:
          "Oui, si vous étiez éligible à un taux réduit mais facturé au taux plein, la réclamation permet de récupérer le trop-versé — en pratique sur l'année en cours et les deux précédentes. Chaque année qui passe fait perdre un exercice récupérable : vérifiez sans attendre.",
      },
    ],
    ctaLabel: 'Vérifier mon éligibilité',
    ctaHref: '/contact?source=agriculteurs&besoin=exoneration-cspe',
    secondaryCta: {
      label: 'Auditer mes factures en parallèle',
      href: '/agriculteurs/comparateur-energie',
    },
  },
];

export function getAideAgricoleStub(slug: string): AideStub | undefined {
  return AIDES_AGRICULTEURS.find((a) => a.slug === slug);
}
